// 서버에 올리는 진행 키와 기기 전용 키의 분류(D5a, docs/ACCOUNT-SAVE-PRD.md §3.1) + 스냅샷 수집·적용·해시.
// 저장 모듈(src/game/*)의 형식은 건드리지 않는다: 원문 문자열을 그대로 묶어 올리고 그대로 되돌려 쓴다.
// Worker도 이 파일을 쓰므로 게임 모듈을 import하지 않는다(키 문자열이 게임 상수와 같은지는 keys.test.ts가 확인).
import { MAX_SNAPSHOT_BYTES, type Snapshot } from './protocol';

/** 서버에 올리는 진행 키(계정 공유). 새 진행 키를 만들면 여기에 넣는다 */
export const SYNC_KEYS: readonly string[] = [
  'dungeonhero.save.v1',
  'dungeonhero.profile.v2',
  'dungeonhero.growth.v1',
  'dungeonhero.growth.v2',
  'dungeonhero.growth.lyra.v1',
  'dungeonhero.skillslots.v1',
  'dungeonhero.skillslots.lyra.v1',
  'dungeonhero.gear.v1',
  'dungeonhero.stage.v1',
  'dungeonhero.stage.hard.v1',
  'dungeonhero.stage.hell.v1', // 헬 난이도 진행(C10)
  'dungeonhero.daily.v1',
  'dungeonhero.hero.v1',
  'dungeonhero.raid.v1', // 레이드 보상 정산 표시(RAID-PRD 2절)
  // 환생(레인 R): 영웅별 회차·포인트 + 회차별 성장 장부
  'dungeonhero.rebirth.v1',
  'dungeonhero.growth.kael.c1.v1',
  'dungeonhero.growth.kael.c2.v1',
  'dungeonhero.growth.lyra.c1.v1',
  'dungeonhero.growth.lyra.c2.v1',
  'dungeonhero.field.v1', // ARPG 필드 균열 조각·균열 클리어(레인 F)
  'dungeonhero.stats.v1', // 자유 스탯 배분(레인 C, src/game/statAlloc.ts)
  'dungeonhero.avatar.v1', // 아바타 보유·장착·조각(레인 E, AVATAR-ADVENTURE-PRD 1.8)
  'dungeonhero.adventure.v1', // 모험 목표판 현상금 완료·도감(레인 E 2단계)
  'dungeonhero.grift.v1', // 대균열 최고 단계·열쇠·이정표(C12, src/game/field/griftSave.ts)
];

/** 기기 전용(설정·진단·이 기능 자신) — 동기화하지 않는다 */
export const LOCAL_ONLY_KEYS: readonly string[] = [
  'dungeonhero.ui.v1',
  'dungeonhero.keys.v1', // 키 배치 프리셋(액션식·PC RPG식, src/shared/keymap.ts)
  'dungeonhero.camera',
  'dungeonhero.display',
  'dungeonhero.audio',
  'dungeonhero.ui.questCard',
  'dungeonhero.ui.size', // PC UI 크기 90/100/115%(기기 전용)
  'dungeonhero.ui.menuMore', // 오른쪽 위 메뉴 「더보기」 펼침(기기 전용, src/ui/menuBar.ts)
  'dungeonhero.ui.rotateHint', // 세로 회전 안내 세션당 1번(sessionStorage, src/ui/rotateHint.ts)
  'dungeonhero.linkHint.v1',
  'dungeonhero.perf.v1',
  'dungeonhero.renderer',
  'dungeonhero.reloads',
  'dungeonhero.gpuFallback', // WebGPU 실행 중 실패로 WebGL2 재부팅한 원인(세션 저장소, 다음 부팅이 보고 — src/render/gpuFallback.ts)
  'dungeonhero.extTried',
  'dungeonhero.inappBannerOff',
  'dungeonhero.iap.tester', // 테스트 결제 상점 보이기(세션 저장소, ?iap=test — src/iap/client.ts)
  'dungeonhero.offers.v1', // 결제 제안 띠를 보인 날·순간별 마지막 시각(기기 전용, src/iap/offers.ts)
  'dungeonhero.iap.playPending', // Play 결제 중 마지막 {orderId, sku, token}(세션 저장소, src/iap/play.ts)
  'dungeonhero.shopSeen.v1', // 마을 장비 탭 본 표시(UI 표시 전용, S7)
  'dungeonhero.rewards.v1', // Web Lock 이름(저장 키 아님)
  'dungeonhero.commerce', // Web Lock 이름(저장 키 아님)
  'dh.look.v1',
  'dungeonhero.account.v1',
  'dungeonhero.account.apply.v1',
  'dungeonhero.account.localBackup.v1',
  'dungeonhero.pwa.v1', // 앱 설치 안내를 봤는지(자산 패치·PWA, 기기 전용)
  // 캐릭터 목록 사본·지금 캐릭터·이관 기록(계정 단위, 정본은 서버 /api/characters — src/characters/roster.ts).
  // 캐릭터별 진행 키는 'dungeonhero.char.<id>.<원래 이름>'(src/characters/keys.ts) — 캐릭터 동기화가 따로 올린다
  'dungeonhero.characters.v1',
];

/** 이 기능의 로컬 키 */
export const ACCOUNT_META_KEY = 'dungeonhero.account.v1';
/** 당겨온 서버 스냅샷 대기(다음 부팅 맨 처음 적용) */
export const ACCOUNT_APPLY_KEY = 'dungeonhero.account.apply.v1';
/** 서버 진행을 적용하기 직전 로컬 진행 사본(마지막 1개) */
export const ACCOUNT_LOCAL_BACKUP_KEY = 'dungeonhero.account.localBackup.v1';

/** 저장 모듈이 쓰는 사본 접미사(동기화 안 함). .good은 적용 때 새 값으로 맞춘다 */
export const COPY_SUFFIXES: readonly string[] = ['.bak', '.bak2', '.latest', '.good', '.bak.latest'];

/**
 * 진행 키 세대(L1029): 세대 1 = 환생 통합(#234) 무렵 들어온 키. 그보다 오래된 빌드(PWA 캐시 탭 등)는 이 키를 몰라서
 * 스냅샷에 빼고 올린다 — 그 '없음'을 지움으로 해석하면 새 빌드의 환생·필드 진행이 사라진다(PR #234 남은 위험).
 * 그래서 스냅샷 g보다 높은 세대의 키는 스냅샷에 없어도 적용(writeSnapshot)이 로컬 값을 지우지 않고, 서버(clampSnapshot)는 이전 값을 이어 둔다.
 * 새 진행 키를 만들면 SYNC_KEYS에 넣고 여기에 SYNC_GEN + 1을 적은 뒤 SYNC_GEN을 올린다(키를 지우는 일은 없다고 본다)
 */
export const SYNC_KEY_GEN: Readonly<Record<string, number>> = {
  'dungeonhero.raid.v1': 1,
  'dungeonhero.rebirth.v1': 1,
  'dungeonhero.growth.kael.c1.v1': 1,
  'dungeonhero.growth.kael.c2.v1': 1,
  'dungeonhero.growth.lyra.c1.v1': 1,
  'dungeonhero.growth.lyra.c2.v1': 1,
  'dungeonhero.field.v1': 1,
  'dungeonhero.stats.v1': 1,
  'dungeonhero.avatar.v1': 1,
  'dungeonhero.adventure.v1': 1,
  // C10 헬 진행: 세대 2. 옛 빌드(g=1) 스냅샷에 이 키가 없어도 '지움'이 아니다(서버·로컬 헬 진행 유지)
  'dungeonhero.stage.hell.v1': 2,
  // C12 대균열: 세대 3. 옛 빌드(g≤2) 스냅샷에 이 키가 없어도 '지움'이 아니다(대균열 기록 유지)
  'dungeonhero.grift.v1': 3,
};
/** 이 빌드가 아는 진행 키 세대 */
export const SYNC_GEN = 3;
/** 서버에 저장이 없을 때의 빈 스냅샷: 서버가 '비었다'고 알려 준 것이므로 모든 세대를 아는 것으로 본다(로컬 잔존 키를 남기지 않는다) */
export const EMPTY_SERVER_SNAPSHOT: Snapshot = { v: 1, keys: {}, g: SYNC_GEN };
/** 키의 세대(표에 없으면 0) */
export function syncKeyGen(key: string): number { return SYNC_KEY_GEN[key] ?? 0; }
/** 스냅샷의 세대(없거나 틀리면 0 = 세대 도입 전 빌드) */
export function snapshotGen(s: Snapshot): number {
  const g = s.g;
  return typeof g === 'number' && Number.isInteger(g) && g >= 0 ? Math.min(g, SYNC_GEN) : 0;
}
/** 스냅샷을 쓴 빌드가 이 키를 알았는가(모르면 '없음'이 지움을 뜻하지 않는다) */
export function snapshotKnows(s: Snapshot, key: string): boolean { return syncKeyGen(key) <= snapshotGen(s); }

const SYNC_SET = new Set(SYNC_KEYS);
/** 다음 빌드가 만들 진행 키 모양(클라이언트 관대 파싱이 모르는 키를 버릴 때만 쓴다) */
const FUTURE_KEY = /^dungeonhero\.[a-z0-9._-]{1,64}$/;
export function isSyncKey(key: string): boolean { return SYNC_SET.has(key); }

export interface KV {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function jsonObject(raw: string): boolean {
  try { const v: unknown = JSON.parse(raw); return v !== null && typeof v === 'object' && !Array.isArray(v); } catch { return false; }
}

/** 지금 로컬 진행 → 스냅샷. 저장소를 읽을 수 없으면 null(빈 진행으로 착각해 서버를 덮지 않게) */
export function collectSnapshot(storage: KV): Snapshot | null {
  const keys: Record<string, string> = {};
  try {
    for (const k of SYNC_KEYS) {
      let v = storage.getItem(k);
      // 주 키가 손상(JSON 객체가 아님)이고 정상 사본(.good)이 있으면 사본을 올린다 — 손상 원문이 서버로 가서 다른 기기의 정상 사본까지 덮지 않게
      // (저장 모듈도 같은 경우 .good으로 잇는다. 불변식 하네스가 찾은 경로: 손상 기기 → 계정 저장 → 다른 기기 .good 덮어씀 → 장비 유실)
      if (v !== null && !jsonObject(v)) { const good = storage.getItem(k + '.good'); if (good !== null && jsonObject(good)) v = good; }
      if (v !== null) keys[k] = v;
    }
  } catch { return null; }
  return { v: 1, keys, g: SYNC_GEN };
}

/** 스냅샷 직렬화 크기(UTF-8 바이트) */
export function snapshotBytes(s: Snapshot): number {
  return new TextEncoder().encode(JSON.stringify(s)).length;
}

/**
 * 형식 검사: 허용 키만, 값은 문자열, 전체 크기 상한. 틀리면 null.
 * lenient(클라이언트가 서버에서 받은 스냅샷): 다음 빌드가 만든 모르는 진행 키는 통째 거부 대신 버린다 — 이 빌드가 쓰지 않는 키라
 * 적용해도 손대지 않고, 다음에 올릴 때 빠져도 서버가 세대 규칙으로 이어 둔다. 서버는 늘 최신이라 엄격하게(기본) 읽는다
 */
export function parseSnapshot(raw: unknown, lenient = false): Snapshot | null {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  if (o.v !== 1 || o.keys === null || typeof o.keys !== 'object' || Array.isArray(o.keys)) return null;
  const keys: Record<string, string> = {};
  for (const [k, v] of Object.entries(o.keys as Record<string, unknown>)) {
    if (typeof v !== 'string') return null;
    if (!SYNC_SET.has(k)) { if (lenient && FUTURE_KEY.test(k)) continue; return null; }
    keys[k] = v;
  }
  const s: Snapshot = { v: 1, keys };
  if (o.g !== undefined) {
    if (typeof o.g !== 'number' || !Number.isInteger(o.g) || o.g < 0 || o.g > 1000) return null;
    s.g = Math.min(o.g, SYNC_GEN);
  }
  return snapshotBytes(s) <= MAX_SNAPSHOT_BYTES ? s : null;
}

/** 정렬한 키·원문의 32비트 해시 두 줄(FNV-1a + murmur 곱, 16진 16자). 같은 진행이면 같은 값. 보안 해시가 아니다(변경 감지 전용) */
export function snapshotHash(s: Snapshot): string {
  let h1 = 0x811c9dc5, h2 = 0x2b5d1a37;
  const feed = (str: string): void => {
    for (let i = 0; i < str.length; i++) {
      const c = str.charCodeAt(i);
      h1 = Math.imul(h1 ^ c, 0x01000193);
      h2 = Math.imul(h2 ^ c, 0x5bd1e995); h2 ^= h2 >>> 15;
    }
    // 구분자: "ab"+"c"와 "a"+"bc"가 같은 값이 되지 않게
    h1 = Math.imul(h1 ^ 0x1f, 0x01000193);
    h2 = Math.imul(h2 ^ 0x1f, 0x5bd1e995);
  };
  for (const k of Object.keys(s.keys).sort()) { feed(k); feed(s.keys[k]); }
  return (h1 >>> 0).toString(16).padStart(8, '0') + (h2 >>> 0).toString(16).padStart(8, '0');
}

/**
 * 스냅샷을 로컬에 그대로 쓴다: 스냅샷의 키는 쓰고, 스냅샷에 없는 진행 키는 지운다(쓴 빌드가 아는 세대의 키만 — snapshotKnows).
 * 각 키의 .good 사본(손상 복구 기준)이 있으면 새 값으로 맞춘다 — 옛 사본이 손상 복구 때 되살아나 진행을 되돌리지 않게.
 * sameOwner=false(다른 계정의 진행 위에 받기)면 세대와 상관없이 스냅샷에 없는 키를 모두 지운다.
 * .bak·.latest(손상 원문 보관)는 건드리지 않는다. 쓰기 예외는 그대로 던진다(호출자가 사본으로 되돌린다).
 */
export function writeSnapshot(storage: KV, s: Snapshot, sameOwner = true): void {
  for (const k of SYNC_KEYS) {
    const v = Object.hasOwn(s.keys, k) ? s.keys[k] : null;
    // 쓴 빌드가 모르는 키(더 높은 세대)는 '없음'이 지움이 아니다: 로컬 값·사본을 그대로 둔다.
    // 단 로컬 진행이 같은 주인(같은 계정)의 것일 때만 — 다른 계정으로 바꿔 받을 때는 이전 계정 진행이 새 계정에 섞이지 않게 지운다(PR #260 리뷰 P1)
    if (v === null && sameOwner && !snapshotKnows(s, k)) continue;
    if (v === null) storage.removeItem(k); else storage.setItem(k, v);
    const good = k + '.good';
    if (storage.getItem(good) !== null) { if (v === null) storage.removeItem(good); else storage.setItem(good, v); }
  }
}

/** 두 스냅샷이 같은 진행인지 */
export function sameSnapshot(a: Snapshot, b: Snapshot): boolean {
  const ka = Object.keys(a.keys), kb = Object.keys(b.keys);
  if (ka.length !== kb.length) return false;
  for (const k of ka) if (a.keys[k] !== b.keys[k]) return false;
  return true;
}

/**
 * 적용 뒤 로컬 스냅샷(got)이 서버 스냅샷(expected)을 그대로 반영했는지: expected가 아는 키는 값·있음/없음이 같아야 하고,
 * expected가 모르는 세대의 키(writeSnapshot이 로컬 값을 남긴 키)는 따지지 않는다
 */
export function appliedMatches(got: Snapshot, expected: Snapshot): boolean {
  for (const k of SYNC_KEYS) {
    const e = Object.hasOwn(expected.keys, k) ? expected.keys[k] : null;
    if (e === null && !snapshotKnows(expected, k)) continue;
    const v = Object.hasOwn(got.keys, k) ? got.keys[k] : null;
    if (v !== e) return false;
  }
  return true;
}
